import type {
  CreateBudgetPaymentInput,
  CreatePlanningTaskInput,
  CreateVenueContactInput,
  CreateVenueInput,
  UpdateBudgetPaymentInput,
  UpdateVenueContactInput,
  UpdateVenueInput,
} from '@amore/shared'
import { unwrap } from '@/lib/api'
import type { DownloadDocumentResponse, PrepareUploadResponse, VenueDetail } from './types'
import { venueWorkspaceApi } from './types'

export function createVenueRequest(slug: string, json: CreateVenueInput) {
  return unwrap<{ venue: VenueDetail }>(venueWorkspaceApi.venues.$post({ param: { slug }, json }))
}

export function updateVenueRequest(slug: string, venueId: string, json: UpdateVenueInput) {
  return unwrap<{ venue: VenueDetail }>(
    venueWorkspaceApi.venues[':venueId'].$patch({ param: { slug, venueId }, json }),
  )
}

export function deleteVenueRequest(slug: string, venueId: string) {
  return venueWorkspaceApi.venues[':venueId'].$delete({ param: { slug, venueId } })
}

export function createVenueTaskRequest(
  slug: string,
  venueId: string,
  json: Omit<CreatePlanningTaskInput, 'context' | 'sortOrder'>,
) {
  return unwrap(
    venueWorkspaceApi['planning-tasks'].$post({
      param: { slug },
      json: { ...json, sortOrder: 0, context: { type: 'venue', id: venueId } },
    }),
  )
}

export function createVenueContactRequest(
  slug: string,
  venueId: string,
  json: CreateVenueContactInput,
) {
  return unwrap(
    venueWorkspaceApi.venues[':venueId'].contacts.$post({ param: { slug, venueId }, json }),
  )
}

export function updateVenueContactRequest(
  slug: string,
  venueId: string,
  contactId: string,
  json: UpdateVenueContactInput,
) {
  return unwrap(
    venueWorkspaceApi.venues[':venueId'].contacts[':contactId'].$patch({
      param: { slug, venueId, contactId },
      json,
    }),
  )
}

export function deleteVenueContactRequest(slug: string, venueId: string, contactId: string) {
  return venueWorkspaceApi.venues[':venueId'].contacts[':contactId'].$delete({
    param: { slug, venueId, contactId },
  })
}

export function createPaymentRequest(
  slug: string,
  expenseId: string,
  json: CreateBudgetPaymentInput,
) {
  return unwrap(
    venueWorkspaceApi['budget-expenses'][':expenseId'].payments.$post({
      param: { slug, expenseId },
      json,
    }),
  )
}

export function updatePaymentRequest(
  slug: string,
  paymentId: string,
  json: UpdateBudgetPaymentInput,
) {
  return unwrap(
    venueWorkspaceApi['budget-payments'][':paymentId'].$patch({
      param: { slug, paymentId },
      json,
    }),
  )
}

export function deletePaymentRequest(slug: string, paymentId: string) {
  return venueWorkspaceApi['budget-payments'][':paymentId'].$delete({
    param: { slug, paymentId },
  })
}

export async function uploadVenueDocument(slug: string, venueId: string, file: File) {
  const prepared = await unwrap<PrepareUploadResponse>(
    venueWorkspaceApi.venues[':venueId'].documents.upload.$post({
      param: { slug, venueId },
      json: {
        originalName: file.name,
        mimeType: file.type || 'application/octet-stream',
        size: file.size,
      },
    }),
  )
  if (!prepared.uploadUrl || !prepared.document) return prepared
  const response = await fetch(prepared.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  })
  if (!response.ok) throw new Error('Soubor se nepodařilo nahrát do úložiště.')
  await unwrap(
    venueWorkspaceApi.venues[':venueId'].documents[':documentId'].complete.$post({
      param: { slug, venueId, documentId: prepared.document.id },
      json: {},
    }),
  )
  return prepared
}

export async function downloadVenueDocument(slug: string, venueId: string, documentId: string) {
  return unwrap<DownloadDocumentResponse>(
    venueWorkspaceApi.venues[':venueId'].documents[':documentId'].download.$get({
      param: { slug, venueId, documentId },
    }),
  )
}

export function deleteVenueDocumentRequest(slug: string, venueId: string, documentId: string) {
  return venueWorkspaceApi.venues[':venueId'].documents[':documentId'].$delete({
    param: { slug, venueId, documentId },
  })
}
